import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import axios from 'axios';
import { getBaseUrl } from '../../shared/utils/apiConfig';

// Backend ReactFlow format (from graph_routes.py)
interface BackendNode {
  id: string;
  type: string;
  position?: { x: number; y: number };
  data: {
    label: string;
    type: string;
    color?: string;
    risk_score?: number;
    criticality?: string;
    status?: string;
    attributes?: Record<string, unknown>;
    first_seen?: string;
    last_seen?: string;
  };
}

interface BackendEdge {
  id: string;
  source: string;
  target: string;
  type: string;
  label?: string;
  data?: {
    relationship_type: string;
    confidence?: number;
    attributes?: Record<string, unknown>;
  };
  animated?: boolean;
}

// Frontend D3 format (for GraphCanvas component)
interface Node {
  id: string;
  type: string;
  label: string;
  risk: number;
  x?: number;
  y?: number;
  fx?: number | null;
  fy?: number | null;
}

interface Link {
  source: string;
  target: string;
  type: string;
  weight: number;
}

interface GraphData {
  nodes: Node[];
  links: Link[];
}

// Transform backend ReactFlow format to frontend D3 format
function transformBackendToFrontend(backendNodes: BackendNode[], backendEdges: BackendEdge[]): GraphData {
  const nodes: Node[] = backendNodes.map(node => ({
    id: node.id,
    type: node.data.type || node.type,
    label: node.data.label,
    risk: node.data.risk_score || 0,
    x: node.position?.x,
    y: node.position?.y,
    fx: null,
    fy: null
  }));

  const links: Link[] = backendEdges.map(edge => ({
    source: edge.source,
    target: edge.target,
    type: edge.data?.relationship_type || edge.type,
    weight: edge.data?.confidence || 0.5
  }));

  return { nodes, links };
}

interface GraphState {
  data: GraphData | null;
  loading: boolean;
  error: string | null;
}

const initialState: GraphState = {
  data: null,
  loading: false,
  error: null
};

// Async thunk for fetching graph data for a specific alert
export const fetchGraphData = createAsyncThunk<GraphData, string | undefined>(
  'graph/fetchGraphData',
  async (alertId: string | undefined = undefined, { rejectWithValue }) => {
    try {
      if (!alertId) {
        return { nodes: [], links: [] };
      }

      // Use real backend endpoint: GET /api/graph/alerts/{alert_id}/entities
      const endpoint = `${getBaseUrl()}/api/graph/alerts/${alertId}/entities`;
      const authToken = localStorage.getItem('astrapeak_auth_token');
      const tenantId = localStorage.getItem('tenant_id');

      const response = await axios.get(endpoint, {
        headers: {
          'Authorization': `Bearer ${authToken}`,
          'X-Tenant-ID': tenantId || '',
          'Content-Type': 'application/json'
        }
      });

      // Backend returns { nodes, edges, metadata }
      // Transform ReactFlow format to D3 format for GraphCanvas
      const backendNodes: BackendNode[] = response.data.nodes || [];
      const backendEdges: BackendEdge[] = response.data.edges || [];
      
      return transformBackendToFrontend(backendNodes, backendEdges);
    } catch (error: unknown) {
      const axiosError = error as { response?: { data?: { message?: string; detail?: string } } };
      return rejectWithValue(
        axiosError.response?.data?.message || 
        axiosError.response?.data?.detail || 
        'Failed to fetch graph data'
      );
    }
  }
);

// Async thunk for fetching neighborhood data for a specific node (entity)
export const fetchNodeNeighborhood = createAsyncThunk<GraphData, string>(
  'graph/fetchNodeNeighborhood',
  async (entityId: string, { rejectWithValue }) => {
    try {
      // Use real backend endpoint: GET /api/graph/entities/{entity_id}/traverse?depth=1
      const endpoint = `${getBaseUrl()}/api/graph/entities/${entityId}/traverse?depth=1`;
      const authToken = localStorage.getItem('astrapeak_auth_token');
      const tenantId = localStorage.getItem('tenant_id');

      const response = await axios.get(endpoint, {
        headers: {
          'Authorization': `Bearer ${authToken}`,
          'X-Tenant-ID': tenantId || '',
          'Content-Type': 'application/json'
        }
      });

      // Backend returns { nodes, edges, depth, start_entity }
      // Transform ReactFlow format to D3 format for GraphCanvas
      const backendNodes: BackendNode[] = response.data.nodes || [];
      const backendEdges: BackendEdge[] = response.data.edges || [];
      
      return transformBackendToFrontend(backendNodes, backendEdges);
    } catch (error: unknown) {
      const axiosError = error as { response?: { data?: { message?: string; detail?: string } } };
      return rejectWithValue(
        axiosError.response?.data?.message || 
        axiosError.response?.data?.detail || 
        'Failed to fetch node neighborhood'
      );
    }
  }
);

const graphSlice = createSlice({
  name: 'graph',
  initialState,
  reducers: {
    clearGraphData: (state) => {
      state.data = null;
      state.error = null;
    },
    updateNodePosition: (state, action: PayloadAction<{ id: string; x: number; y: number }>) => {
      if (state.data && state.data.nodes) {
        const { id, x, y } = action.payload;
        const nodeIndex = state.data.nodes.findIndex(node => node.id === id);
        
        if (nodeIndex !== -1) {
          const updatedNode = { ...state.data.nodes[nodeIndex] };
          updatedNode.x = x;
          updatedNode.y = y;
          state.data.nodes[nodeIndex] = updatedNode;
        }
      }
    }
  },
  extraReducers: (builder) => {
    builder
      // Handle fetchGraphData
      .addCase(fetchGraphData.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchGraphData.fulfilled, (state, action) => {
        state.loading = false;
        state.data = action.payload;
      })
      .addCase(fetchGraphData.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      
      // Handle fetchNodeNeighborhood
      .addCase(fetchNodeNeighborhood.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchNodeNeighborhood.fulfilled, (state, action) => {
        state.loading = false;
        
        // Merge new neighborhood data with existing graph data
        if (state.data) {
          // Filter out duplicate nodes
          const existingNodeIds = new Set(state.data.nodes.map(node => node.id));
          const newNodes = action.payload.nodes.filter(
            (node: Node) => !existingNodeIds.has(node.id)
          );
          
          // Filter out duplicate links
          const existingLinkKeys = new Set(
            state.data.links.map(link => `${link.source}-${link.target}-${link.type}`)
          );
          const newLinks = action.payload.links.filter(
            (link: Link) => !existingLinkKeys.has(`${link.source}-${link.target}-${link.type}`)
          );
          
          state.data = {
            nodes: [...state.data.nodes, ...newNodes],
            links: [...state.data.links, ...newLinks]
          };
        } else {
          state.data = action.payload;
        }
      })
      .addCase(fetchNodeNeighborhood.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });
  }
});

export const { clearGraphData, updateNodePosition } = graphSlice.actions;
export default graphSlice.reducer;
